import { SidebarSimpleIcon } from "@foreday/ui/components/icons";
import { cn } from "@foreday/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  isRedirect,
  Outlet,
  retainSearchParams,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import {
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { BottomBar } from "@/client/components/bottom-bar";
import {
  AskForeday,
  ForedayPanel,
  focusForeday,
  isInForeday,
  leaveForeday,
  useForedayShortcut,
} from "@/client/components/foreday-slot";
import { LazySlot } from "@/client/components/lazy-slot";
import { OfflineBar } from "@/client/components/offline-bar";
import {
  PanelActionsSlot,
  PanelLeadSlot,
} from "@/client/components/panel-lead";
import { PhoneHeader } from "@/client/components/phone-header";
import { PlanBarSlot } from "@/client/components/plan-bar-slot";
import { BootPending } from "@/client/components/route-states";
import { ShellDialogs } from "@/client/components/shell-dialogs";
import { SideSlotContext, SLOT_COLUMN } from "@/client/components/side-slot";
import { Sidebar } from "@/client/components/sidebar";
import { TaskPanelSlot, taskPanel } from "@/client/components/task-panel-slot";
import { TodayLine } from "@/client/components/today-line";
import { clientCopy } from "@/client/copy";
import { loadKeyedPopups } from "@/client/lib/every-chunk";
import { registry } from "@/client/lib/keyboard/registry";
import { MAIN_PANEL_ID } from "@/client/lib/main-panel";
import { keepAcrossReloads } from "@/client/lib/persist";
import { settingsWindow, shortcutSheet } from "@/client/lib/popups";
import { planBar } from "@/client/lib/screen-parts";
import { ensureTenantData, requireCompany } from "@/client/lib/session";
import { rememberShell } from "@/client/lib/shell-boot";
import {
  setSidebarCollapsed,
  useSidebarCollapsed,
} from "@/client/lib/sidebar-collapsed";
import { useFreshness } from "@/client/lib/sync";
import { tasksQuery } from "@/client/lib/tasks-query";
import { holdToastsOver } from "@/client/lib/toast-area";
import { useFollowActiveCompany } from "@/client/lib/use-follow-active-company";
import { isPhone, useMatches, usePhone } from "@/client/lib/use-phone";
import { openForedaySearch } from "@/features/agent/panel-search";
import { needsPlanBar } from "@/features/billing/needs-plan-bar";
import { peopleQuery } from "@/features/people/people-query";
import { openSettingsSearch } from "@/features/settings/settings-search";
import { commentsQuery } from "@/features/tasks/comments-query";
import { QuickAdd, QuickAddRound } from "@/features/tasks/components/quick-add";
import { projectsQuery } from "@/features/tasks/projects-query";
import { openTaskSearch } from "@/features/tasks/schemas";
import { taskActivityQuery, taskQuery } from "@/features/tasks/task-query";

const threadOf = (search: object) =>
  "foreday" in search && typeof search.foreday === "string"
    ? search.foreday
    : undefined;

// How long the shell waits for a part it would rather draw whole, the open settings page or the
// plan bar: enough for a usual connection, too little for a stalled one to hold the screen.
const SETTLE_MS = 300;

/** Screens whose own create button is the primary action, so the round Add task stays off them. */
const OWN_CREATE: ReadonlySet<string> = new Set([
  "/_authenticated/$slug/invoices/",
  "/_authenticated/$slug/projects/",
]);
const settled = (part: Promise<unknown>) =>
  Promise.race([
    part.catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, SETTLE_MS)),
  ]);

/**
 * The slug is a readable label, not the tenant: every request is scoped by the session, and this
 * guard only makes the address bar and the session agree.
 */
export const Route = createFileRoute("/_authenticated/$slug")({
  validateSearch: openTaskSearch
    .extend(openSettingsSearch.shape)
    .extend(openForedaySearch.shape),
  search: {
    // Off a phone the conversation stays open from screen to screen; on one a link closes it.
    middlewares: [
      (context) =>
        isPhone()
          ? context.next(context.search)
          : retainSearchParams<{ foreday?: string }>(["foreday"])(context),
    ],
  },
  beforeLoad: async ({ context, params, location, preload }) => {
    const session = await requireCompany(
      context.queryClient,
      params.slug,
      location.href,
      preload,
    );
    // Restored before any child loader runs, or it would fetch what this browser already holds.
    // Skipped on preload: the tab has not moved to this company yet.
    if (!preload) {
      await keepAcrossReloads(
        context.queryClient,
        session.userId,
        session.companyId,
      ).catch(() => undefined);
    }
    return session;
  },
  loaderDeps: ({ search }) => ({
    task: search.task,
    settings: search.settings,
  }),
  loader: async ({ context, deps }) => {
    // The panel opens at once and shows its own loading state for the rest.
    const quietly = (read: Promise<unknown>) =>
      void read.catch(() => undefined);
    // The pages' registry and their nav stay out of every screen's first load, so the shell waits
    // a moment for the open page, its code and its reads, and the window opens on the page, not a
    // skeleton. Off a phone the menu, which the sidebar, the palette and the switcher open, shows
    // the first page it lists; on a phone it is the list alone.
    const registry = deps.settings
      ? Promise.all([
          import("@/features/settings/settings-nav"),
          import("@/client/components/settings-pages"),
        ])
      : null;
    const opened = registry?.then(([nav, { SETTINGS_PAGES }]) => {
      const settings =
        deps.settings === "menu"
          ? isPhone()
            ? undefined
            : nav.settingsNavFor(context)[0]?.key
          : deps.settings;
      return settings !== undefined && nav.maySeeSettings(context, settings)
        ? SETTINGS_PAGES[settings].load(context)
        : undefined;
    });
    if (registry && opened) {
      // Every other page the window lists follows the open one, so a first visit to each is ready.
      const rest = () =>
        registry.then(([nav, { SETTINGS_PAGES }]) => {
          for (const item of nav.settingsNavFor(context)) {
            quietly(SETTINGS_PAGES[item.key].load(context));
          }
        });
      quietly(opened.finally(rest));
    }
    // The window's own code too: pressed before every chunk has loaded, it has nothing to draw.
    const page = deps.settings
      ? settled(Promise.all([settingsWindow.load(), opened]))
      : null;
    // The bar's code is fetched beside the screen's reads, so a usual load draws it whole; a slower
    // or failed one draws its plainer notice in its place.
    const plan = needsPlanBar(context) ? settled(planBar.load()) : null;
    // Read beside every screen's own reads, so a screen's project chips and people never pop in. A
    // failed read leaves each screen to read it again and fail in its own place, not the shell's.
    const kept = (failure: unknown) => {
      if (isRedirect(failure)) throw failure;
    };
    const workingSet = [
      ensureTenantData(context.queryClient, projectsQuery).catch(kept),
      ensureTenantData(context.queryClient, peopleQuery).catch(kept),
    ];
    if (!deps.task) {
      await Promise.all([page, plan, ...workingSet]);
      return;
    }
    quietly(taskPanel.load());
    quietly(ensureTenantData(context.queryClient, taskQuery(deps.task)));
    quietly(ensureTenantData(context.queryClient, commentsQuery(deps.task)));
    quietly(
      ensureTenantData(context.queryClient, taskActivityQuery(deps.task)),
    );
    await Promise.all([
      page,
      plan,
      ...workingSet,
      ensureTenantData(context.queryClient, tasksQuery),
    ]);
  },
  // The list skeleton belongs inside the shell, which this route is still deciding to draw.
  pendingComponent: BootPending,
  component: CompanyShell,
});

/**
 * Keyed by slug so a company switch remounts everything: the cache is emptied on a switch, and a
 * screen still mounted would keep showing the previous company's data.
 */
function CompanyShell() {
  const { slug } = Route.useParams();
  const { userId, companyTimezone, companyForedayOn } = Route.useRouteContext();
  const { task, foreday: thread } = Route.useSearch();
  const phone = usePhone();
  const { data: people = [] } = useQuery(peopleQuery);
  // Opened on a conversation's address, by a reload or a link, the panel shows it.
  const [foreday, setForeday] = useState(
    companyForedayOn === true && thread !== undefined,
  );
  // Foreday and the shortcuts share one column: opening either closes the other.
  const [shortcuts, setShortcuts] = useState(false);
  const [lead, setLead] = useState<HTMLDivElement | null>(null);
  const [actions, setActions] = useState<HTMLDivElement | null>(null);
  // Alone in the slot, Foreday slides out and the slot keeps it until the slide ends; "over" when it
  // lay over the screen, so it leaves from there.
  const [leaving, setLeaving] = useState<"beside" | "over" | null>(null);
  // Opened afresh each time, so the caret lands in the box even on a reopen mid-slide.
  const [opens, setOpens] = useState(0);
  // Focus leaves the panel before it unmounts, however it was closed.
  const closeForeday = () => {
    leaveForeday();
    if (foreday && !phone && items.length === 0) {
      setLeaving(over ? "over" : "beside");
    }
    setForeday(false);
  };
  const openForeday = () => {
    setShortcuts(false);
    setLeaving(null);
    // Asked again while open, from the palette or search, the panel keeps its draft and answer.
    if (!foreday) setOpens((count) => count + 1);
    setForeday(true);
    if (covered) {
      flushSync(() => setItemOnTop(false));
      focusForeday();
    } else setItemOnTop(false);
  };
  const toggleForeday = () =>
    foreday && !covered ? closeForeday() : openForeday();
  // Quick add and the palette open from keys that work before Home's reads land.
  useEffect(loadKeyedPopups, []);
  // On a phone the panel covers the screen, so a link pressed, even the open page's tab, or a move
  // made from a field (search, a task) closes it. Subscribed rather than read from the router's
  // state, which would re-render the shell on every navigation.
  const router = useRouter();
  const phoneNow = useRef(phone);
  const threadNow = useRef(thread);
  useEffect(() => {
    phoneNow.current = phone;
    threadNow.current = thread;
  });
  useEffect(() => {
    // Pressing the open item's row, by click or key, brings it forward from under Foreday; a reload
    // (`router.invalidate`, as Try again runs) reaches the same event. A press counts only while it
    // is being dispatched and outside Foreday.
    let press: Event | undefined;
    const pressed = (event: Event) => {
      // A click a key press dispatches on its way (a picker opening) keeps that key as the press.
      if (press && press.eventPhase !== Event.NONE) return;
      press = isInForeday(event.target) ? undefined : event;
    };
    document.addEventListener("click", pressed, true);
    document.addEventListener("keydown", pressed, true);
    const stop = router.subscribe(
      "onBeforeNavigate",
      ({ hrefChanged, fromLocation, toLocation }) => {
        if (!hrefChanged && press && press.eventPhase !== Event.NONE) {
          setItemOnTop(true);
        }
        if (!hrefChanged || !phoneNow.current) return;
        // The panel moving between its own conversations is not a move off it.
        if (
          fromLocation?.pathname === toLocation.pathname &&
          threadOf(fromLocation.search) !== threadOf(toLocation.search)
        ) {
          return;
        }
        leaveForeday();
        setForeday(false);
      },
    );
    // Back or Forward off a conversation's address is the screen without the panel.
    const stepped = router.history.subscribe(
      ({
        action,
        location,
      }: {
        action: { type: string };
        location: Parameters<typeof router.parseLocation>[0];
      }) => {
        if (action.type === "PUSH" || action.type === "REPLACE") return;
        const opens = threadOf(router.parseLocation(location).search);
        if (opens === undefined && threadNow.current !== undefined) {
          leaveForeday();
          setForeday(false);
        }
      },
    );
    return () => {
      document.removeEventListener("click", pressed, true);
      document.removeEventListener("keydown", pressed, true);
      stop();
      stepped();
    };
  }, [router]);
  const openShortcuts = () => {
    leaveForeday();
    setForeday(false);
    setShortcuts(true);
    setItemOnTop(false);
  };
  // A conversation the address comes to name opens; a panel closed by any path leaves the address.
  const [seenThread, setSeenThread] = useState(thread);
  if (seenThread !== thread) {
    setSeenThread(thread);
    if (companyForedayOn && thread !== undefined) setForeday(true);
  }
  useEffect(() => {
    if (foreday || thread === undefined) return;
    void router.navigate({
      to: ".",
      search: (prev: object) => ({ ...prev, foreday: undefined }),
      replace: true,
    });
  });
  useForedayShortcut(
    companyForedayOn ? { toggle: toggleForeday, open: openForeday } : null,
  );

  // The task, a leave ask or a day open in the slot, and Foreday or the shortcuts: one shows at a
  // time, the one opened last. The other is kept, and closing the one shown brings it back
  // (`docs/design/shell/panels-at-every-width.md`).
  const [items, setItems] = useState<readonly (() => void)[]>([]);
  const [itemOnTop, setItemOnTop] = useState(false);
  const [itemColumn, setItemColumn] = useState<HTMLDivElement | null>(null);
  // One function for the shell's life: a panel claims once, not again on every render.
  const [claim] = useState(() => (close: () => void) => {
    setItems((before) => [...before, close]);
    setItemOnTop(true);
    return () => setItems((before) => before.filter((one) => one !== close));
  });
  // The task panel stays mounted from one task to the next, so a new task is an opening too. It
  // comes forward as the panel draws it, which is deferred, so the old one never shows between.
  const drawnTask = useDeferredValue(task);
  const [shownTask, setShownTask] = useState(drawnTask);
  if (shownTask !== drawnTask) {
    setShownTask(drawnTask);
    if (drawnTask !== undefined) setItemOnTop(true);
  }
  const side =
    companyForedayOn && foreday ? "foreday" : shortcuts ? "shortcuts" : null;
  // A task in the address counts before its panel claims the slot, so moving from another item to a
  // task never shows Foreday between the two.
  const covered = itemOnTop && (items.length > 0 || task !== undefined);
  // Loaded with a task in the address, the slot holds its width from the first paint and only
  // slides, so the screen beside it is not laid out again on every frame of the entrance.
  const [landedOnTask, setLandedOnTask] = useState(task !== undefined);
  if (landedOnTask && task === undefined) setLandedOnTask(false);
  const panelOpen = items.length > 0 || side !== null || landedOnTask;
  const slot = { node: itemColumn, claim, open: panelOpen };
  const closeTop = () => {
    if (covered) items.at(-1)?.();
    else if (side === "foreday") closeForeday();
    else if (side === "shortcuts") setShortcuts(false);
    else items.at(-1)?.();
  };

  // Below `rail` an open panel turns the sidebar to icons. Opened by hand there, the full sidebar
  // would leave the screen under its 576 px floor, so the panel lies over the screen instead.
  // Below 55.5rem the full sidebar alone would, so it stays icons.
  const collapsed = useSidebarCollapsed(userId);
  const [expanded, setExpanded] = useState(false);
  const narrow = useMatches("(width < 55.5rem)");
  const squeezed = useMatches("(width < 83.5rem)") && panelOpen;
  if (expanded && !squeezed) setExpanded(false);
  const icons = narrow || collapsed || (squeezed && !expanded);
  const over = squeezed && expanded;
  // Anything opening ends the slide out; so does nothing being left to run (see the sidebar).
  if (leaving && panelOpen) setLeaving(null);
  const overNow = over || leaving === "over";
  // Marked on the root, where the toasts read it to clear the panel (`globals.css`).
  useLayoutEffect(() => {
    if (phone || !panelOpen) return;
    const root = document.documentElement;
    root.dataset.sideSlot = over ? "over" : "";
    return () => {
      delete root.dataset.sideSlot;
    };
  }, [phone, panelOpen, over]);
  const slotNode = useRef<HTMLDivElement>(null);
  const settle = () => {
    if (slotNode.current?.getAnimations().length === 0) setLeaving(null);
  };
  useEffect(() => {
    if (leaving && slotNode.current?.getAnimations().length === 0) {
      setLeaving(null);
    }
  }, [leaving]);
  const toggleSidebar = () => {
    if (narrow) return;
    setSidebarCollapsed(userId, !icons);
    setExpanded(icons && squeezed);
  };
  const toggleHeld = useRef(toggleSidebar);
  useEffect(() => {
    toggleHeld.current = toggleSidebar;
  });
  // A phone has no sidebar to toggle.
  useEffect(
    () =>
      phone
        ? undefined
        : registry.register({
            id: "sidebar.toggle",
            title: clientCopy.nav.toggle,
            group: clientCopy.palette.groups.actions,
            icon: SidebarSimpleIcon,
            shortcut: "[",
            run: () => toggleHeld.current(),
          }),
    [phone],
  );
  const ownCreate = useRouterState({
    select: (state) => state.matches.some((one) => OWN_CREATE.has(one.routeId)),
  });
  useEffect(() => rememberShell(slug, collapsed), [slug, collapsed]);
  useFollowActiveCompany();
  useFreshness();

  if (phone) {
    return (
      <div
        key={slug}
        // Held to the screen while Foreday is open, so its conversation scrolls inside the panel.
        // A table's header row sticks under the phone header, on the page's own fill.
        className={cn(
          "flex flex-col [--table-head-bg:var(--background)] [--table-head-top:var(--header-h)]",
          foreday ? "h-dvh" : "min-h-dvh",
        )}
        onClickCapture={(event) => {
          if (
            foreday &&
            event.target instanceof Element &&
            event.target.closest("a[href]")
          ) {
            closeForeday();
          }
        }}
      >
        <PhoneHeader
          slug={slug}
          foreday={
            companyForedayOn
              ? { open: foreday, onToggle: toggleForeday }
              : undefined
          }
        />
        <OfflineBar />
        <PlanBarSlot />
        {companyForedayOn && foreday ? (
          <ForedayPanel onClose={closeForeday} />
        ) : task ? (
          <TaskPanelSlot />
        ) : (
          <>
            <Outlet />
            {ownCreate ? null : (
              <>
                {/* Room under the last row for the round + and the gap it keeps above the tab bar
                    (`QuickAddRound`), so scrolled to the end nothing is left under it. */}
                <div
                  aria-hidden="true"
                  className="h-[calc(var(--control-h-lg)+1.25rem)] shrink-0"
                />
                <QuickAddRound />
              </>
            )}
          </>
        )}
        <BottomBar slug={slug} />
        <QuickAdd people={people} />
        <ShellDialogs />
      </div>
    );
  }

  return (
    // Clipped across, so a panel sliding in past the right edge never scrolls the page.
    <div
      key={slug}
      className="canvas flex h-dvh gap-1 overflow-x-clip py-4 pr-4"
    >
      <Sidebar
        slug={slug}
        icons={icons}
        onToggle={narrow ? undefined : toggleSidebar}
        onShortcuts={openShortcuts}
      />
      <SideSlotContext value={slot}>
        <div className="relative flex min-w-0 flex-1">
          <div
            id={MAIN_PANEL_ID}
            ref={holdToastsOver}
            // `--table-head-top` is the pinned header's height: its top padding and its one row. The
            // gutter is kept, so a screen that scrolls lays out like one that does not.
            className="group/main @container/main isolate flex [--table-head-top:calc(1.875rem+var(--size-touch))] [scrollbar-gutter:stable] min-w-0 flex-1 flex-col overflow-y-auto rounded-panel border border-edge bg-card shadow-lift-1"
          >
            <OfflineBar />
            <PlanBarSlot />
            {/* Pinned while the panel scrolls, as the scrolled list frames draw it. */}
            <header className="sticky top-0 z-30 shrink-0 bg-card px-[var(--pad-panel-x)] pt-7.5">
              {/* Across the whole panel, so Ask Foreday stands in one place on every screen. */}
              <div className="flex min-h-[var(--size-touch)] items-center justify-between gap-4">
                {/* A screen's way back, when it has one, stands where the date does. */}
                <div className="min-w-0">
                  <div ref={setLead} className="peer empty:hidden" />
                  <div className="peer-not-empty:hidden">
                    {/* Only a day-based page has a date (`page.tsx` marks the panel). */}
                    <div className="hidden group-data-dated/main:block">
                      <TodayLine timeZone={companyTimezone} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div ref={setActions} className="contents" />
                  {companyForedayOn && (
                    <AskForeday
                      open={foreday && !covered}
                      onToggle={toggleForeday}
                    />
                  )}
                </div>
              </div>
            </header>
            <PanelLeadSlot value={lead}>
              <PanelActionsSlot value={actions}>
                <Outlet />
              </PanelActionsSlot>
            </PanelLeadSlot>
          </div>
          {/* Below `over` the panel lies over the screen; Escape is the keyboard's way out. */}
          {(panelOpen || leaving) && (
            <div
              aria-hidden="true"
              onClick={closeTop}
              className={cn(
                "absolute inset-0 z-40 hidden rounded-panel bg-overlay transition-opacity duration-[var(--duration-move)] ease-drawer starting:opacity-0 max-over:block",
                overNow && "block",
                !panelOpen && "pointer-events-none opacity-0",
              )}
            />
          )}
        </div>
        <TaskPanelSlot />
      </SideSlotContext>
      {/* The slot comes in from the right edge. Beside the screen it also takes its width from the
          screen as it comes, so the screen narrows with it rather than at once. */}
      <div
        ref={slotNode}
        data-slot-column
        inert={!panelOpen}
        onTransitionEnd={settle}
        onTransitionCancel={settle}
        className={cn(
          "gap-1 transition-[margin,translate] duration-[var(--duration-move)] ease-drawer max-over:fixed max-over:inset-y-4 max-over:right-4 max-over:z-40",
          overNow
            ? "fixed inset-y-4 right-4 z-40 starting:translate-x-[calc(100%+1rem)]"
            : landedOnTask
              ? "starting:translate-x-[calc(100%+1rem)]"
              : "starting:-mr-[calc(var(--side-slot-w)+0.25rem)] starting:translate-x-5 max-over:starting:mr-0 max-over:starting:translate-x-[calc(100%+1rem)]",
          !panelOpen &&
            (overNow
              ? "translate-x-[calc(100%+1rem)]"
              : "-mr-[calc(var(--side-slot-w)+0.25rem)] translate-x-5 max-over:mr-0 max-over:translate-x-[calc(100%+1rem)]"),
          panelOpen || leaving ? "flex" : "hidden",
        )}
      >
        {/* Another item opened while one shows takes its place without moving the column. */}
        <div
          ref={setItemColumn}
          className={cn(
            SLOT_COLUMN,
            "[&>*:not(:last-child)]:hidden",
            // Kept while a task in the address is on its way, so the slot holds its width.
            items.length === 0 && !landedOnTask && !covered && "hidden",
            side && !covered && "hidden",
          )}
        />
        {/* Hidden, not unmounted, under an item, so the conversation and its draft are kept. */}
        <div className={cn("contents", covered && "hidden")}>
          {(side === "foreday" || leaving) && (
            <ForedayPanel key={opens} onClose={closeForeday} />
          )}
          {side === "shortcuts" && (
            <LazySlot
              of={shortcutSheet}
              props={{ onClose: () => setShortcuts(false) }}
              fallback={<div className={SLOT_COLUMN} />}
              onFailed={() => setShortcuts(false)}
            />
          )}
        </div>
      </div>
      <QuickAdd people={people} />
      <ShellDialogs />
    </div>
  );
}
